import { useEffect } from 'react';
import CommonHeader from '@/components/feature/CommonHeader';
import FloatingCta from '@/components/feature/FloatingCta';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import SeoHead from '@/components/feature/SeoHead';
import PhilosophyHero from './components/PhilosophyHero';
import PhilosophyToc from './components/PhilosophyToc';
import PhilosophyChapter01 from './components/PhilosophyChapter01';
import PhilosophyChapter02 from './components/PhilosophyChapter02';
import PhilosophyChapter03 from './components/PhilosophyChapter03';
import PhilosophyChapter04 from './components/PhilosophyChapter04';
import PhilosophyChapter05 from './components/PhilosophyChapter05';
import PhilosophyChapter06 from './components/PhilosophyChapter06';
import PhilosophyClosing from './components/PhilosophyClosing';
import PhilosophyAccess from './components/PhilosophyAccess';
import PhilosophyCta from './components/PhilosophyCta';
import CommonFooter from '@/components/feature/CommonFooter';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const philosophyJsonLd = [
  {
    '@type': 'WebPage',
    '@id': `${siteUrl}/philosophy/`,
    url: `${siteUrl}/philosophy/`,
    name: '矯正治療の考え方｜moyuk SAPPORO 札幌｜なぜ「判断するための場所」なのか',
    description: '札幌moyuk SAPPOROが大切にする矯正治療への向き合い方。治療を勧めることが目的ではなく、患者様が自分で納得して選べるための情報と時間を提供します。',
    inLanguage: 'ja',
    isPartOf: { '@id': `${siteUrl}/#website` },
    author: {
      '@type': 'Dentist',
      name: 'moyuk SAPPORO',
      '@id': `${siteUrl}/#dentist`,
    },
  },
  {
    '@type': 'Article',
    '@id': `${siteUrl}/philosophy/#article`,
    headline: 'なぜ「判断するための場所」なのか — moyuk SAPPOROの矯正相談哲学',
    description: '矯正治療は人生の選択。押しつけではなく、情報と時間を提供することで患者様自身が最善の判断をできる環境を作ることがmoyuk SAPPOROの使命です。',
    author: {
      '@type': 'Dentist',
      name: 'moyuk SAPPORO',
      '@id': `${siteUrl}/#dentist`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'moyuk SAPPORO',
      '@id': `${siteUrl}/#dentist`,
      url: siteUrl,
    },
    inLanguage: 'ja',
    url: `${siteUrl}/philosophy/`,
    dateModified: '2026-04-14',
  },
  {
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '診断を無料にする理由は何ですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '「先に申し込みではなく、先に診断が医療の本来の姿」だからです。患者様が自分の歯の状態や治療の向き不向きを知らずに契約することを避けるため、診断・レポート作成・ご説明まですべて無料にしています。',
        },
      },
      {
        '@type': 'Question',
        name: '治療を勧めるのではなく「判断する場所」とはどういう意味ですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '当院は治療を売ることを目的とせず、患者様が納得して自分で選択できる情報と時間を提供することを目的としています。レポートをお持ち帰りいただき、ご家族と相談したり他院と比較したりしてから決定していただいて構いません。',
        },
      },
      {
        '@type': 'Question',
        name: '矯正治療の費用はどのように決まりますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '矯正の範囲や使用するシステム、治療期間によって異なります。当院では無料診断でお口の状態を確認した後、複数のプランと費用の目安をレポートにまとめてご提示しています。',
        },
      },
    ],
  },
];

export default function PhilosophyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="矯正治療の考え方・なぜ「判断するための場所」なのか | moyuk SAPPORO さいわいデンタル"
        description="さいわいデンタルが「診断を無料にする理由」「治療を売らない理由」を詳しく解説。マウスピース矯正の向き不向き・費用の決まり方・タイミングの考え方まで。10年後のあなたに選択肢を残す矯正歯科の考え方。"
        keywords="矯正 考え方 札幌,歯科矯正 セカンドオピニオン 札幌,マウスピース矯正 向き不向き,矯正 費用 相場 札幌,矯正 いつ始める,さいわいデンタル,moyuk SAPPORO"
        canonical={`${siteUrl}/philosophy/`}
        ogType="article"
        ogTitle="矯正治療の考え方・なぜ「判断するための場所」なのか | moyuk SAPPORO さいわいデンタル"
        ogDescription="「先に申し込みではなく、先に診断が医療の本来の姿」。マウスピース矯正の向き不向き・費用・タイミングについて、医学的根拠をもとに解説。治療を売らない歯科医師の考え方。"
        ogSiteName="moyuk SAPPORO さいわいデンタル"
        ogLocale="ja_JP"
        ogImageWidth="1200"
        ogImageHeight="630"
        twitterCard="summary_large_image"
        twitterTitle="矯正治療の考え方 | moyuk SAPPORO さいわいデンタル"
        twitterDescription="診断を無料にする理由、治療を急かさない理由。マウスピース矯正の向き不向きを医学的根拠で解説。10年後のあなたに選択肢を残す矯正歯科。"
        jsonLd={philosophyJsonLd}
      />
      <CommonHeader />
      <main className="pt-20">
        <PhilosophyHero />
        <PhilosophyToc />
        <PhilosophyChapter01 />
        <PhilosophyChapter02 />
        <PhilosophyChapter03 />
        <PhilosophyChapter04 />
        <PhilosophyChapter05 />
        <PhilosophyChapter06 />
        <PhilosophyClosing />
        <PhilosophyAccess />
        <PhilosophyCta />
      </main>
      <CommonFooter />
      <FloatingCta href="https://reservation.stransa.co.jp/8878b04a60c23f1ce5b3268e265263cb" label="無料診断を予約する" />
      <MobileBottomNav />
    </div>
  );
}
